'use client';

import type { Theme, SxProps } from '@mui/material/styles';

import NextLink from 'next/link';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import {
  Box,
  Stack,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import Logo from 'src/components/logo/Logo';
import { Iconify } from 'src/components/iconify';
import { LanguageSwitcher } from 'src/components/language-switcher';

import { Footer } from './footer';

import { SignInButton } from '../components/sign-in-button';

const NAV_HEIGHT = 68;

export type MainLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

export function MainLayout({ sx, children }: MainLayoutProps) {
  const theme = useTheme();
  const t = useTranslations('Navigation');
  const pathname = usePathname();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links: { label: string; href: string }[] = [
    { label: t('home'), href: '/' },
    { label: t('contact'), href: paths.contact },
  ];

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', ...sx }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: NAV_HEIGHT,
          justifyContent: 'center',
          backgroundImage: 'none',
          bgcolor: scrolled ? 'rgba(20, 26, 33, 0.96)' : 'rgba(20, 26, 33, 0.72)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${alpha('#ffffff', 0.06)}`,
          color: 'common.white',
          transition: 'background-color 0.3s',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            maxWidth: 1200,
            width: '100%',
            mx: 'auto',
            px: { xs: 2.5, md: 4 },
            minHeight: { xs: NAV_HEIGHT, md: NAV_HEIGHT },
          }}
        >
          {/* Logo + wordmark — single link to home */}
          <Stack
            component={NextLink}
            href="/"
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ flexGrow: 0, textDecoration: 'none', color: 'inherit' }}
          >
            <Logo disabledLink width={36} height={36} />
            <Typography
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                fontSize: 18,
                color: 'common.white',
                letterSpacing: '-0.3px',
              }}
            >
              Testelope
            </Typography>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop nav links */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}
          >
            {links.map((link) => (
              <Typography
                key={link.href}
                component={RouterLink}
                href={link.href}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: isActive(link.href) ? 'common.white' : alpha('#ffffff', 0.72),
                  textDecoration: 'none',
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 1,
                  transition: 'color 0.2s, background 0.2s',
                  '&:hover': { color: 'common.white', bgcolor: alpha('#ffffff', 0.08) },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Stack>

          {/* Right actions */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <LanguageSwitcher />
            </Box>
            <SignInButton
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                px: 2.5,
                py: 0.875,
                borderRadius: 1,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                border: 'none',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            />
            <IconButton
              aria-label="Menu"
              onClick={() => setMobileOpen(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: alpha('#ffffff', 0.85),
              }}
            >
              <Iconify icon="solar:hamburger-menu-bold" width={22} />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={isMobile && mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { bgcolor: 'grey.900', color: 'common.white', width: 280, p: 2 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Logo disabledLink width={32} height={32} />
            <Typography
              sx={{ fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 16 }}
            >
              Testelope
            </Typography>
          </Stack>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'common.white' }}>
            <Iconify icon="mingcute:close-line" width={22} />
          </IconButton>
        </Stack>
        <Stack spacing={0.5}>
          {links.map((link) => (
            <Typography
              key={link.href}
              component={RouterLink}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              sx={{
                fontSize: 15,
                fontWeight: 500,
                color: isActive(link.href) ? 'common.white' : alpha('#ffffff', 0.72),
                textDecoration: 'none',
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                '&:hover': { bgcolor: alpha('#ffffff', 0.06), color: 'common.white' },
              }}
            >
              {link.label}
            </Typography>
          ))}
          <Box sx={{ mt: 2, px: 1.5 }}>
            <LanguageSwitcher />
          </Box>
        </Stack>
      </Drawer>

      {/* Spacer for fixed AppBar — only when content does NOT start with the dark hero (which is full-bleed and absorbs the nav) */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Footer />
    </Box>
  );
}
